import { progressInstance } from '@nx-paeonia/utils-events';
import { isErr, mapErr } from '@nx-paeonia/utils-result';
import { addAttributesToCart, addToCart, type Cart, getCart } from '@nx-paeonia/utils-shopify';
import { css, html, LitElement } from 'lit';
import { property } from 'lit/decorators/property.js';

export class CheckoutButtonComponent extends LitElement {
  @property({ type: String, attribute: 'label' })
  label = 'Checkout';

  @property({ type: String, attribute: 'checkout-url' })
  checkoutUrl = '/checkout';

  @property({ type: String, attribute: 'delivery-cost-product-id' })
  deliveryCostProductId = '';

  protected override render() {
    return html`<a @click="${this.handleClick}">${this.label}</a>`;
  }

  /**
   * Custom click handler for the checkout button.
   *
   * This button contains all logic to make sure an order is as complete as possible.
   *
   * It loads the current cart and checks if it has any fleurop products.
   *   If so, it checks if the cart delivery date is set and valid.
   *   If so, it checks if the delivery costs product is also added. Or adds it automatically.
   *
   * If everything is fine, it redirects to the checkout page.
   */
  private async handleClick(event: Event) {
    event.preventDefault();

    progressInstance.start();

    // Custom click handler logic requires us to look if the cart contains a fleurop product
    const cartResponse = await getCart();

    mapErr(cartResponse, (err) => {
      console.warn(err);
    });

    if (isErr(cartResponse)) {
      progressInstance.done();
      return;
    }

    const cart = cartResponse.data;

    if (cart.item_count === 0) {
      progressInstance.done();
      return;
    }

    await this.checkAndAddDeliveryCostsProductIfNeeded(cart);
    await this.checkAndAddDateIfNeeded(cart);

    progressInstance.done();
    globalThis.location.href = this.checkoutUrl;
  }

  /**
   * Check if the supplied cart contains any fleurop products and adds the delivery costs product if needed.
   */
  private async checkAndAddDeliveryCostsProductIfNeeded(cart: Cart) {
    const hasFleuropProduct = cart.items.some(
      (item) => item.sku?.toLowerCase().startsWith('fl_') && !item.sku?.toLowerCase().startsWith('fl_s'),
    );

    if (hasFleuropProduct) {
      // Check if delivery costs product is already in cart
      const hasDeliveryCostsProduct = cart.items.some((item) => item.variant_id === this.deliveryCostProductId);

      if (!hasDeliveryCostsProduct) {
        // We now need to add the product to the cart.
        const res = await addToCart({ id: this.deliveryCostProductId, quantity: 1 });

        mapErr(res, (err) => {
          console.warn(err);
        });
      }
    }
  }

  /**
   * Check if the cart has the _DeliveryDate attribute set.
   *
   * If not check whether "Date" is set as cart attribute and make a Date object out of it. Then convert it to YYYY-MM-DD and set it as _DeliveryDate.
   * The "Date" field is formatted as a localized date without a year.
   */
  private async checkAndAddDateIfNeeded(cart: Cart) {
    const hasDeliveryDateAttribute = cart.attributes?._DeliveryDate;
    if (hasDeliveryDateAttribute) {
      return;
    }

    const dateAttribute = cart.attributes?.Date;
    if (!dateAttribute) {
      return;
    }

    const formattedDate = this.decideDeliveryDate(dateAttribute);
    if (!formattedDate) {
      return;
    }

    // Now we can set the _DeliveryDate attribute
    const updatedAttributes = { ...cart.attributes, _DeliveryDate: formattedDate };
    await addAttributesToCart(updatedAttributes);
  }

  static override get styles() {
    return [
      css`
        :host {
          display: flex;
          margin-top: 1rem !important;
        }

        a {
          display: block;
          background: var(--accent-1);
          color: var(--text);
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          font-size: 1.25rem;
          cursor: pointer;
          transition: background-color 0.3s ease;
          width: 100%;
          text-decoration: none;

          &:hover {
            background-color: color-mix(in srgb, var(--accent-1) 80%, transparent);
          }
        }
      `,
    ];
  }

  /**
   * Decide on the delivery date based on the "Date" cart attribute. The "Date" field is formatted as a localized date without a year.
   * The function tries to parse the date and returns it in YYYY-MM-DD format. If parsing fails, it returns an empty string.
   *
   * It takes the current year into account when parsing, because in december a date can be picked for next year it
   * can roll the date over to the next year. For example, if today is December 30th and the user picks January 5th,
   * it should be parsed as January 5th of the next year, not the current year as the API requires a full date with year.
   *
   * TODO: Move to a generic helper file where this can be tested using jest.
   */
  decideDeliveryDate(dateAttribute: string): string {
    const currentYear = new Date().getFullYear();
    const parsedDate = new Date(`${dateAttribute} ${currentYear}`); // Append a dummy year for parsing

    if (parsedDate.getMonth() === 0 && new Date().getMonth() === 11) {
      // If the parsed month is January and the current month is December, it means the date is for the next year.
      parsedDate.setFullYear(currentYear + 1);
    }

    if (Number.isNaN(parsedDate.getTime())) {
      return '';
    }

    // Format to YYYY-MM-DD
    const yyyy = parsedDate.getFullYear();
    const mm = String(parsedDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const dd = String(parsedDate.getDate()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;
  }
}

export function defineCheckoutButtonComponent(): void {
  if (!customElements.get('y-checkout-button')) {
    customElements.define('y-checkout-button', CheckoutButtonComponent);
  }
}
